import { Link } from 'react-router-dom';
import { Select } from 'antd';
import { Moment } from 'moment';
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    DeleteOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { SquareButton } from '../../../../components/action-buttons';
import { Button } from '../../../../components/button';
import { DatePicker } from '../../../../components/date-picker';
import { LabeledField } from '../../../../components/form-fields/labeled-field';
import { Section } from '../../../../components/form-fields/section';
import style from './style.module.scss';

const DATE_FORMAT = 'YYYY-MM-DD';

const formatDate = (date: Moment | null) =>
    date ? date.format(DATE_FORMAT) : null;

export type ReusableCouponRow = {
    /**
     * Client-side key, and React's list key across a reorder. Keying on the
     * index would hand a moved row the previous occupant's mounted `Select`,
     * along with its focus and open dropdown.
     */
    uid: string;
    id?: number;
    promotionId: number | null;
    /**
     * The API marks every coupon in this section unlimited redemption, so the
     * flag is never edited here; the section membership is the decision.
     */
    startDate: string | null;
    endDate: string | null;
    promotionName?: string | null;
    /** Shown beside the index for rows seeded from a saved club. */
    thumbnail?: string | null;
};

let rowUidCounter = 0;

export function nextReusableCouponRowUid(): string {
    rowUidCounter += 1;
    return `reusable-coupon-row-uid-${rowUidCounter}`;
}

export const REUSABLE_COUPON_REWARD_TYPE = 'reusable_coupon';

export type ReusableCouponTierReward = {
    id: number;
    rewardType: string;
    promotionId?: number | null;
    promotionName?: string | null;
    promotionImage?: string | null;
    position: number;
    startDate?: string | null;
    endDate?: string | null;
};

/**
 * Ordered on `position` rather than on the order the club response arrived in:
 * `position` is what the app renders by, so it is the order members see.
 */
export function reusableCouponRowsFromTierRewards(
    tierRewards: ReusableCouponTierReward[]
): ReusableCouponRow[] {
    return tierRewards
        .filter((reward) => reward.rewardType === REUSABLE_COUPON_REWARD_TYPE)
        .sort((a, b) => a.position - b.position || a.id - b.id)
        .map((reward) => ({
            uid: nextReusableCouponRowUid(),
            id: reward.id,
            promotionId: reward.promotionId ?? null,
            startDate: reward.startDate ?? null,
            endDate: reward.endDate ?? null,
            promotionName: reward.promotionName ?? null,
            thumbnail: reward.promotionImage ?? null,
        }));
}

export type ReusableCouponPromotion = {
    id: number;
    title: string;
    /** Only coupon promotions can be attached as reusable coupons. */
    type: string;
    /** The API refuses unlimited redemption on a coupon that is not private. */
    isPrivate: boolean | null;
    thumbnail: string | null;
    temperatureActivationFloor: number | null;
    temperatureActivationCeiling: number | null;
};

export type ReusableCouponRowErrors = {
    promotionId?: string;
    privacy?: string;
    dates?: string;
};

/** Null-checked rather than truthiness-checked: 0 C is a real bound. */
function temperatureRangePhrase(
    floor: number | null,
    ceiling: number | null
): string | null {
    if (floor != null && ceiling != null) {
        return `${floor} to ${ceiling} C`;
    }
    if (floor != null) {
        return `${floor} C and up`;
    }
    if (ceiling != null) {
        return `up to ${ceiling} C`;
    }
    return null;
}

export function reusableCouponOptionLabel(
    promotion: ReusableCouponPromotion
): string {
    const range = temperatureRangePhrase(
        promotion.temperatureActivationFloor,
        promotion.temperatureActivationCeiling
    );
    return range ? `${promotion.title} (weather ${range})` : promotion.title;
}

export function reusableCouponWeatherLabel(
    promotion: ReusableCouponPromotion
): string | null {
    const range = temperatureRangePhrase(
        promotion.temperatureActivationFloor,
        promotion.temperatureActivationCeiling
    );
    return range ? `Weather ${range}` : null;
}

/**
 * The client-side half of what the replace endpoint enforces, so a mistake
 * lands on the offending row instead of arriving as a failed request. Keyed by
 * row index.
 *
 * `promotions` is the visible-coupon list the picker was built from. It is
 * optional because a caller that only needs the duplicate and window rules has
 * no list to pass; the privacy rule then stays silent rather than guessing, and
 * the API remains the backstop.
 */
export function validateReusableCoupons(
    rows: ReusableCouponRow[],
    promotions: ReusableCouponPromotion[] = []
): Record<number, ReusableCouponRowErrors> {
    const seen = new Set<number>();
    const duplicated = new Set<number>();
    rows.forEach((row) => {
        if (row.promotionId == null) {
            return;
        }
        if (seen.has(row.promotionId)) {
            duplicated.add(row.promotionId);
        } else {
            seen.add(row.promotionId);
        }
    });

    const errors: Record<number, ReusableCouponRowErrors> = {};
    rows.forEach((row, index) => {
        const rowErrors: ReusableCouponRowErrors = {};
        if (row.promotionId == null) {
            rowErrors.promotionId = 'Select a coupon.';
        } else if (duplicated.has(row.promotionId)) {
            rowErrors.promotionId = 'This coupon is already in the list.';
        }
        const promotion = promotions.find(
            (candidate) => candidate.id === row.promotionId
        );
        if (
            promotion &&
            promotion.type !== 'coupon' &&
            !rowErrors.promotionId
        ) {
            // Keep the duplicate warning when both rules hit, or the operator
            // fixes the type and only then learns the row is duplicated.
            rowErrors.promotionId = 'Select a coupon promotion.';
        }
        if (promotion && promotion.isPrivate !== true) {
            rowErrors.privacy =
                'Coupons here are scanned unlimited times, which needs a private coupon. Open the coupon page, turn Winnable on to make it private, save it, then add it here.';
        }
        if (
            row.startDate &&
            row.endDate &&
            new Date(row.startDate) > new Date(row.endDate)
        ) {
            rowErrors.dates =
                'The start date must be on or before the end date.';
        }
        if (Object.keys(rowErrors).length > 0) {
            errors[index] = rowErrors;
        }
    });
    return errors;
}

/** Drops the display-only fields; array order is what carries the position. */
export function buildReusableCouponPayload(rows: ReusableCouponRow[]) {
    return rows.map((row) => ({
        ...(row.id == null ? {} : { id: row.id }),
        promotionId: row.promotionId as number,
        startDate: row.startDate,
        endDate: row.endDate,
    }));
}

export interface UnlimitedCouponsProps {
    rows: ReusableCouponRow[];
    promotions: ReusableCouponPromotion[];
    isLoadingPromotions?: boolean;
    /**
     * The row errors, computed once by the page that also decides whether
     * Save is enabled, so the button and the rendered messages are one
     * decision and cannot drift apart.
     */
    errors: Record<number, ReusableCouponRowErrors>;
    onChange: (rows: ReusableCouponRow[]) => void;
}

export const UnlimitedCoupons = ({
    rows,
    promotions,
    isLoadingPromotions,
    errors,
    onChange,
}: UnlimitedCouponsProps) => {
    const options = promotions
        .filter((promotion) => promotion.type === 'coupon')
        .map((promotion) => ({
            value: promotion.id,
            label: reusableCouponOptionLabel(promotion),
        }));

    // A coupon that has expired or been hidden drops out of
    // `/promotions/visible` while the row still holds it, so carry it as an
    // option rather than letting the Select fall back to a bare id.
    const optionsForRow = (row: ReusableCouponRow) => {
        if (
            row.promotionId == null ||
            options.some((option) => option.value === row.promotionId)
        ) {
            return options;
        }
        return [
            ...options,
            {
                value: row.promotionId,
                label: row.promotionName ?? `Promotion #${row.promotionId}`,
            },
        ];
    };

    const patchRow = (index: number, patch: Partial<ReusableCouponRow>) =>
        onChange(
            rows.map((row, i) => (i === index ? { ...row, ...patch } : row))
        );

    const moveRow = (index: number, offset: number) => {
        const target = index + offset;
        if (target < 0 || target >= rows.length) {
            return;
        }
        const reordered = [...rows];
        const [moved] = reordered.splice(index, 1);
        reordered.splice(target, 0, moved);
        onChange(reordered);
    };

    const removeRow = (index: number) =>
        onChange(rows.filter((_, i) => i !== index));

    const addRow = () =>
        onChange([
            ...rows,
            {
                uid: nextReusableCouponRowUid(),
                promotionId: null,
                startDate: null,
                endDate: null,
            },
        ]);

    const weatherLabelForRow = (row: ReusableCouponRow) => {
        const promotion = promotions.find(
            (candidate) => candidate.id === row.promotionId
        );
        return promotion ? reusableCouponWeatherLabel(promotion) : null;
    };

    return (
        <Section title="Unlimited coupons">
            {rows.length === 0 ? (
                <p className={style.empty}>
                    No unlimited coupons yet. Add one to have it show in the
                    club.
                </p>
            ) : (
                <div className={style.list}>
                    {rows.map((row, index) => {
                        const rowErrors = errors[index];
                        const weatherLabel = weatherLabelForRow(row);
                        const position = index + 1;
                        // A picked coupon's image wins over the one the club
                        // response came with, which can be stale after an edit.
                        const thumbnail =
                            promotions.find(
                                (candidate) => candidate.id === row.promotionId
                            )?.thumbnail ?? row.thumbnail;
                        return (
                            <div
                                className={style.row}
                                key={row.uid}
                                data-testid={`reusable-coupon-row-${index}`}
                            >
                                <div className={style.position}>
                                    {thumbnail && (
                                        <img
                                            className={style.thumbnail}
                                            src={thumbnail}
                                            alt=""
                                        />
                                    )}
                                </div>

                                <div className={style.fields}>
                                    <LabeledField
                                        label="Coupon*"
                                        hasError={!!rowErrors?.promotionId}
                                        className={style.coupon_field}
                                    >
                                        <Select<number>
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Select a coupon"
                                            aria-label={`Coupon ${position}`}
                                            style={{ width: '100%' }}
                                            value={row.promotionId ?? undefined}
                                            loading={isLoadingPromotions}
                                            onChange={(value) =>
                                                patchRow(index, {
                                                    promotionId: value,
                                                    promotionName: null,
                                                    thumbnail:
                                                        promotions.find(
                                                            (candidate) =>
                                                                candidate.id ===
                                                                value
                                                        )?.thumbnail ?? null,
                                                })
                                            }
                                            options={optionsForRow(row)}
                                        />
                                    </LabeledField>
                                    <LabeledField
                                        label="Start date"
                                        hasError={!!rowErrors?.dates}
                                    >
                                        <DatePicker
                                            showTime={false}
                                            format={DATE_FORMAT}
                                            value={row.startDate}
                                            onChange={(date) =>
                                                patchRow(index, {
                                                    startDate: formatDate(date),
                                                })
                                            }
                                        />
                                    </LabeledField>
                                    <LabeledField
                                        label="End date"
                                        hasError={!!rowErrors?.dates}
                                    >
                                        <DatePicker
                                            showTime={false}
                                            format={DATE_FORMAT}
                                            value={row.endDate}
                                            onChange={(date) =>
                                                patchRow(index, {
                                                    endDate: formatDate(date),
                                                })
                                            }
                                        />
                                    </LabeledField>
                                </div>

                                <div className={style.actions}>
                                    <SquareButton
                                        type="text"
                                        ariaLabel={`Move coupon ${position} up`}
                                        icon={<ArrowUpOutlined />}
                                        disabled={index === 0}
                                        onClick={() => moveRow(index, -1)}
                                    />
                                    <SquareButton
                                        type="text"
                                        ariaLabel={`Move coupon ${position} down`}
                                        icon={<ArrowDownOutlined />}
                                        disabled={index === rows.length - 1}
                                        onClick={() => moveRow(index, 1)}
                                    />
                                    <SquareButton
                                        type="text"
                                        ariaLabel={`Remove coupon ${position}`}
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeRow(index)}
                                    />
                                </div>

                                {weatherLabel && (
                                    <span className={style.weather}>
                                        {weatherLabel}
                                    </span>
                                )}

                                {row.promotionId != null && (
                                    <Link
                                        className={style.open_link}
                                        to={`/coupon/edit/${row.promotionId}`}
                                    >
                                        Open coupon
                                    </Link>
                                )}

                                {rowErrors?.promotionId && (
                                    <span className={style.error}>
                                        {rowErrors.promotionId}
                                    </span>
                                )}

                                {rowErrors?.privacy && (
                                    <span className={style.error}>
                                        {rowErrors.privacy}
                                    </span>
                                )}

                                {rowErrors?.dates && (
                                    <span className={style.error}>
                                        {rowErrors.dates}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className={style.add}>
                <Button
                    variant="secondary"
                    icon={<PlusOutlined />}
                    onClick={addRow}
                >
                    Add unlimited coupon
                </Button>
            </div>
        </Section>
    );
};
